import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, batch } from 'react-redux';

import { SvgIcon } from '../../../../../../../common/react/components/SvgIcon';
import { STATS_LEADERS_TAB_NAME } from '../../stats-leaders/config/routing';
import { STATS_LEADERS_CATEGORY_COLUMN_MAP } from '../../stats-leaders/config/category-column-map';
import { navLink } from '../../../utils/nav-link';
import { StatsCard } from '../../common/StatsCard';
import { getTranslation } from '../../../../../../../common/react/utils/translations';
import { STATS_COLUMNS } from '../../common/config/columns';
import {
    UPDATE_DATA_TYPE,
    UPDATE_FILTER_ITEM,
    UPDATE_NAV_ITEM,
    UPDATE_SORT
} from '../../../../../store/js/modules/statspro/stats-leaders/actions';
import { TeamWatermarkBackground } from '../../TeamWatermarkBackground';

/**
 * @param {object} props - Props.
 * @param {Array} props.players - Player details.
 * @param {string} props.compseasonId - Compseason ID.
 * @param props.external
 * @returns {JSX.Element} Component.
 */
export const SeasonLeadersCard = ({ players, compseasonId, external }) => {
    const dispatch = useDispatch();
    const [firstPlayer, ...otherPlayers] = players;
    const fullTableInfo =
        STATS_LEADERS_CATEGORY_COLUMN_MAP[firstPlayer.category];
    const isSeasonAverage = firstPlayer.type === 'SEASON_AVERAGE';
    const translations = {
        category: STATS_COLUMNS[fullTableInfo.column].name,
        perGame: getTranslation('label.statspro.perGame'),
        fullTable: getTranslation('label.statspro.fullTable')
    };

    const firstPlayerURL = PULSE.app.common.url.getPlayerProfileURL(
        firstPlayer.player.playerId
    );

    const linkToFullTable = () => {
        if (external !== 'true') {
            batch(() => {
                dispatch(
                    UPDATE_NAV_ITEM.request({
                        navItem: 'category',
                        value: fullTableInfo.category
                    })
                );

                dispatch(
                    UPDATE_NAV_ITEM.request({
                        navItem: 'season',
                        value: { id: parseInt(compseasonId) }
                    })
                );

                dispatch(
                    UPDATE_NAV_ITEM.request({
                        navItem: 'round',
                        value: { id: -1, number: 0 }
                    })
                );

                dispatch(
                    UPDATE_SORT.request({
                        columnId: fullTableInfo.column,
                        sortDirection: 'descending',
                        force: true
                    })
                );

                dispatch(
                    UPDATE_FILTER_ITEM.request({
                        filterItem: 'teams',
                        value: []
                    })
                );

                dispatch(
                    UPDATE_FILTER_ITEM.request({
                        filterItem: 'positions',
                        value: []
                    })
                );

                dispatch(
                    UPDATE_DATA_TYPE.request({
                        type: isSeasonAverage ? 'averages' : 'totals'
                    })
                );
            });

            navLink(STATS_LEADERS_TAB_NAME);
        } else {
            location.href = PULSE.app.common.url.getStatsLeadersURL(
                fullTableInfo.column,
                parseInt(compseasonId),
                isSeasonAverage ? 'averages' : 'totals'
            );
        }
    };

    return (
        <StatsCard>
            <div className={'stats-card-featured-player'}>
                <div className="stats-card-featured-player__inner-container">
                    <TeamWatermarkBackground
                        teamAbbreviation={firstPlayer.team.teamAbbr}
                        className="stats-card-featured-player__bg stats-card-featured-player__bg--no-border-radius-bottom"
                    />
                    <div className="stats-card-featured-player__category-container">
                        <p className="stats-card-featured-player__category">
                            {translations.category}
                        </p>
                    </div>
                    <div className="stats-card-featured-player__details">
                        <p className="stats-card-featured-player__value">
                            {firstPlayer.value}
                            {isSeasonAverage && (
                                <span> {translations.perGame}</span>
                            )}
                        </p>
                        <div className="stats-card-featured-player__details-bottom">
                            <a
                                href={firstPlayerURL}
                                className="stats-card-featured-player__name"
                            >
                                {firstPlayer.player.givenName}{' '}
                                <span>{firstPlayer.player.surname}</span>
                            </a>
                        </div>
                    </div>
                    <div className="stats-card-featured-player__photo-container">
                        <img
                            className="stats-card-featured-player__image"
                            alt={`${getTranslation(
                                'label.stats.headshot.alt'
                            )} ${firstPlayer.player.givenName} ${
                                firstPlayer.player.surname
                            }`}
                            src={PULSE.app.common.image.migratePlayerPhotoURL(
                                firstPlayer.player.photoURL,
                                0.6
                            )}
                        />
                    </div>
                </div>
            </div>

            <div className="stats-card-table">
                {otherPlayers.map((player, i) => {
                    return (
                        <div
                            key={i}
                            className={
                                'stats-card-table__player team-' +
                                player.team.teamAbbr.toLowerCase()
                            }
                        >
                            <div className="stats-card-table__photo-container">
                                <img
                                    className="stats-card-table__image"
                                    alt={`${getTranslation(
                                        'label.stats.headshot.alt'
                                    )} ${player.player.givenName} ${
                                        player.player.surname
                                    }`}
                                    src={PULSE.app.common.image.migratePlayerPhotoURL(
                                        player.player.photoURL
                                    )}
                                />
                            </div>
                            <a
                                href={PULSE.app.common.url.getPlayerProfileURL(
                                    player.player.playerId
                                )}
                                className="stats-card-table__name"
                            >
                                {player.player.givenName}{' '}
                                <span>{player.player.surname}</span>
                            </a>
                            <div className="stats-card-table__value">
                                {player.value}
                            </div>
                        </div>
                    );
                })}

                <div className="stats-card-table__link-container">
                    <button
                        className="stats-card-table__link"
                        onClick={() => linkToFullTable()}
                    >
                        {translations.fullTable}
                        <span className="stats-card-table__link-icon">
                            <SvgIcon icon="arrow-right" className="icon" />
                        </span>
                    </button>
                </div>
            </div>
        </StatsCard>
    );
};

SeasonLeadersCard.propTypes = {
    players: PropTypes.array.isRequired,
    compseasonId: PropTypes.string.isRequired,
    external: PropTypes.string.isRequired
};
